import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Box,
  IconButton,
  TextField,
  Tooltip,
  useTheme,
  Theme,
} from '@material-ui/core'
import {
  Close as CloseIcon,
  Send as SendIcon,
  Assistant as AssistantIcon,
  Add as AddIcon,
  History as HistoryIcon,
  DeleteOutline as DeleteIcon,
  ConfirmationNumberOutlined as ConfirmationNumberIcon,
  ImageOutlined as ImageIcon,
} from '@material-ui/icons'
import ReactMarkdown from 'react-markdown'
// Tables, strikethrough and autolinks are GitHub-flavoured markdown, which
// react-markdown does not parse without this plugin - without it the model's
// tables render as raw pipe characters.
import remarkGfm from 'remark-gfm'

import env from 'src/env'
import { GlobalContext } from 'src/context/global'
import {
  streamAiChat,
  fetchMyTickets,
  fetchConversationUpdates,
  AiChatTurn,
  SupportTicket,
} from 'src/api/ai'
import getUseStyles from './styles'
import { pickImages, MAX_IMAGES, PickedImage } from './images'
import {
  ChatSession,
  MAX_SESSIONS,
  MIN_WIDTH,
  MAX_WIDTH,
  loadSessions,
  saveSessions,
  loadWidth,
  saveWidth,
  newSession,
  titleFor,
  hasConsented,
  recordConsent,
} from './sessions'

const SUGGESTIONS = [
  'How did revenue do last week?',
  'Why are ads not serving?',
  'Which countries have the best eCPM?',
]

const TOOL_LABELS: Record<string, string> = {
  summarizing: 'Recalling earlier context',
  list_games: 'Looking up your games',
  get_release_status: 'Checking integration status',
  get_metrics_summary: 'Reading performance summary',
  get_metrics_timeseries: 'Reading daily trends',
  get_metrics_by_country: 'Breaking down by country',
  get_retention: 'Checking retention',
  get_installs: 'Checking installs',
}

/**
 * Markdown parsing is not cheap, and during streaming the message list
 * re-renders on every chunk. Memoising on the text means only the message that
 * actually changed is re-parsed, instead of the whole conversation each time.
 */
const MessageBubble = memo(
  ({
    role,
    content,
    at,
    images,
    classes,
    isStreaming,
  }: {
    role: 'user' | 'assistant' | 'support'
    content: string
    at?: number
    images?: { url: string; name?: string }[]
    classes: Record<string, string>
    isStreaming: boolean
  }) => {
    const thumbs = images?.length ? (
      <Box className={classes.thumbRow}>
        {images.map((img, i) => (
          <a key={i} href={img.url} target="_blank" rel="noreferrer">
            <img
              src={img.url}
              alt={img.name || 'attachment'}
              className={classes.thumb}
            />
          </a>
        ))}
      </Box>
    ) : null

    if (role === 'support') {
      return (
        <Box className={classes.supportRow}>
          <Box className={classes.supportAvatar}>Y1</Box>
          <Box className={classes.supportBody}>
            <Box className={classes.supportHeader}>
              <span className={classes.supportName}>Yodo1 Support Team</span>
              {at && (
                <span className={classes.supportTime}>
                  {new Date(at).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              )}
            </Box>
            <Box className={classes.supportBubble}>
              {content}
              {thumbs}
            </Box>
          </Box>
        </Box>
      )
    }
    return (
      <Box
        className={
          role === 'user' ? classes.userBubble : classes.assistantBubble
        }
      >
        {role === 'user' ? (
          <>
            {content}
            {thumbs}
          </>
        ) : isStreaming ? (
          // While the answer is still arriving, show plain text. Re-parsing a
          // growing markdown document on every flush is the most expensive
          // thing in the panel, and it is thrown away on the next chunk.
          <div className={classes.streamingText}>{content}</div>
        ) : (
          <ReactMarkdown
            className={classes.markdown}
            remarkPlugins={[remarkGfm]}
          >
            {content}
          </ReactMarkdown>
        )}
      </Box>
    )
  }
)
MessageBubble.displayName = 'MessageBubble'

const AiAssistant = (): JSX.Element | null => {
  const theme: Theme = useTheme()
  const useStyles = getUseStyles(theme)
  const classes = useStyles()

  const [open, setOpen] = useState(false)
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [drawer, setDrawer] = useState<'none' | 'chats' | 'tickets'>('none')
  const [input, setInput] = useState('')
  const [pending, setPending] = useState<PickedImage[]>([])
  const [imgError, setImgError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [streaming, setStreaming] = useState(false)
  const streamingRef = useRef(false)
  const [activity, setActivity] = useState<string | null>(null)
  const [handledByHuman, setHandledByHuman] = useState(false)
  const lastSupportAtRef = useRef(0)
  const wasInterceptedRef = useRef(false)
  // The poll effect must not list `send` as a dependency (it would re-subscribe
  // on every render), so reach it through a ref that is always current.
  const sendRef = useRef<
    ((text: string, opts?: { resend?: boolean }) => void) | null
  >(null)
  const [width, setWidth] = useState(420)
  const [consented, setConsented] = useState(false)
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const openTicketCount = useMemo(
    () => tickets.filter(t => t.status !== 'done').length,
    [tickets]
  )

  const abortRef = useRef<AbortController | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null)

  const { state: globalState } = useContext(GlobalContext)
  // Stable per-account id. Chats are keyed on this so one account never sees
  // another's conversations on a shared browser.
  const userKey: string =
    globalState?.userInfo?.developer_key || globalState?.userInfo?.email || ''

  useEffect(() => {
    setWidth(loadWidth())
  }, [])

  // Reload whenever the signed-in account changes - including the sign-out
  // transition, where userKey becomes empty and the panel must show nothing.
  useEffect(() => {
    if (!userKey) {
      setSessions([])
      setActiveId(null)
      return
    }
    const stored = loadSessions(userKey)
    const initial = stored.length > 0 ? stored : [newSession()]
    setSessions(initial)
    setActiveId(initial[0].id)
    setConsented(hasConsented(userKey))
  }, [userKey])

  // Refresh tickets when the panel opens and after each completed turn, so a
  // ticket the agent just raised appears without a reload.
  const loadTickets = useCallback(() => {
    if (!userKey) return
    fetchMyTickets()
      .then(setTickets)
      .catch(() => undefined)
  }, [userKey])

  useEffect(() => {
    if (open && consented) loadTickets()
  }, [open, consented, loadTickets])

  // A support reply arrives with no request from us, so poll for it while the
  // panel is open. Cheap: one small GET every 10s, only when visible.
  useEffect(() => {
    if (!open || !consented || !activeId) return undefined
    let cancelled = false

    const poll = async () => {
      const { intercepted, messages } = await fetchConversationUpdates(
        activeId,
        lastSupportAtRef.current
      )
      if (cancelled) return
      setHandledByHuman(intercepted)

      // Handed back to the agent. If the customer's last message was never
      // answered - they asked something while a human was handling it, or the
      // takeover happened mid-question - the agent should pick it up rather
      // than leave them waiting for a reply that never comes.
      const handedBack = wasInterceptedRef.current && !intercepted
      wasInterceptedRef.current = intercepted
      if (handedBack && !streamingRef.current) {
        const msgs =
          sessionsRef.current.find(x => x.id === activeId)?.messages ?? []
        const last = msgs[msgs.length - 1]
        if (last?.role === 'user' && last.content.trim()) {
          sendRef.current?.(last.content, { resend: true })
        }
      }
      if (messages.length > 0) {
        lastSupportAtRef.current = Math.max(...messages.map(m => m.at))
        updateActive(sess => {
          // Dedupe by id: the panel remounts on close/open, which resets the
          // cursor and would otherwise re-append every support reply.
          const seen = new Set(sess.messages.map(m => m.id).filter(Boolean))
          const fresh = messages.filter(m => !seen.has(m.id))
          if (fresh.length === 0) return sess
          return {
            ...sess,
            messages: [
              ...sess.messages,
              ...fresh.map(m => ({
                role: 'support' as const,
                content: m.text,
                id: m.id,
                at: m.at,
                ...(m.images?.length ? { images: m.images } : {}),
              })),
            ],
            updatedAt: Date.now(),
          }
        })
      }
    }

    poll()
    const timer = setInterval(poll, 10_000)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, consented, activeId])

  const active = useMemo(
    () => sessions.find(s => s.id === activeId) ?? null,
    [sessions, activeId]
  )

  const persist = useCallback(
    (next: ChatSession[]) => {
      setSessions(next)
      saveSessions(userKey, next)
    },
    [userKey]
  )

  // Keep the newest message in view as tokens arrive.
  //
  // Reading scrollHeight forces a synchronous layout, so doing it inline on
  // every flush stalls the frame. Defer to rAF (after layout has happened
  // anyway), and leave the user alone if they have scrolled up to read.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return undefined
    const frame = requestAnimationFrame(() => {
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight
      if (distanceFromBottom > 120) return
      el.scrollTo({
        top: el.scrollHeight,
        behavior: streaming ? 'auto' : 'smooth',
      })
    })
    return () => cancelAnimationFrame(frame)
  }, [active?.messages, activity, streaming])

  useEffect(() => () => abortRef.current?.abort(), [])

  // Keep the latest sessions reachable from cleanup without re-running effects.
  const sessionsRef = useRef<ChatSession[]>([])
  useEffect(() => {
    sessionsRef.current = sessions
  }, [sessions])

  // Persist on a trailing debounce. During streaming every token changes state,
  // so the timer keeps resetting and only one write happens once output settles.
  useEffect(() => {
    if (sessions.length === 0) return undefined
    if (!userKey) return undefined
    const timer = setTimeout(() => saveSessions(userKey, sessions), 600)
    return () => clearTimeout(timer)
  }, [sessions, userKey])

  // The debounce above is cancelled on unmount, so flush whatever is pending.
  const userKeyRef = useRef('')
  useEffect(() => {
    userKeyRef.current = userKey
  }, [userKey])
  useEffect(
    () => () => {
      if (userKeyRef.current && sessionsRef.current.length > 0) {
        saveSessions(userKeyRef.current, sessionsRef.current)
      }
    },
    []
  )

  // ---- resize -------------------------------------------------------------
  useEffect(() => {
    const onMove = (event: MouseEvent) => {
      if (!resizeRef.current) return
      // The panel is anchored right, so dragging left grows it.
      const delta = resizeRef.current.startX - event.clientX
      const next = Math.min(
        MAX_WIDTH,
        Math.max(MIN_WIDTH, resizeRef.current.startWidth + delta)
      )
      setWidth(next)
    }
    const onUp = () => {
      if (!resizeRef.current) return
      resizeRef.current = null
      document.body.style.userSelect = ''
      setWidth(current => {
        saveWidth(current)
        return current
      })
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [])

  const startResize = (event: React.MouseEvent) => {
    event.preventDefault()
    resizeRef.current = { startX: event.clientX, startWidth: width }
    // Stops the drag from selecting page text behind the panel.
    document.body.style.userSelect = 'none'
  }

  // ---- sessions -----------------------------------------------------------
  const createSession = () => {
    if (streaming) return
    const fresh = newSession()
    // Oldest chat drops out once the limit is reached.
    const next = [fresh, ...sessions].slice(0, MAX_SESSIONS)
    persist(next)
    setActiveId(fresh.id)
    setDrawer('none')
    setInput('')
  }

  const deleteSession = (id: string) => {
    const next = sessions.filter(s => s.id !== id)
    const ensured = next.length > 0 ? next : [newSession()]
    persist(ensured)
    if (id === activeId) setActiveId(ensured[0].id)
  }

  // Deliberately does NOT persist: this runs once per streamed token, and a
  // synchronous localStorage write per token froze the whole page. Persistence
  // is handled by the debounced effect below.
  const updateActive = useCallback(
    (mutate: (session: ChatSession) => ChatSession) => {
      setSessions(prev => prev.map(s => (s.id === activeId ? mutate(s) : s)))
    },
    [activeId]
  )

  // ---- sending ------------------------------------------------------------
  const send = useCallback(
    async (text: string, opts?: { resend?: boolean }) => {
      const question = text.trim()
      if (!question || streaming || !active) return

      // On a resend the question is already the last message in the thread;
      // appending it again would show the customer their own words twice.
      const attachments = opts?.resend ? [] : pending
      const history: AiChatTurn[] = opts?.resend
        ? [...active.messages]
        : [
            ...active.messages,
            {
              role: 'user' as const,
              content: question,
              ...(attachments.length ? { images: attachments } : {}),
            },
          ]
      const summaryAtSend = active.summary

      updateActive(s => ({
        ...s,
        messages: [...history, { role: 'assistant', content: '' }],
        title:
          s.messages.length === 0
            ? titleFor({ ...s, messages: history })
            : s.title,
        updatedAt: Date.now(),
      }))
      setInput('')
      setPending([])
      setImgError(null)
      setStreaming(true)
      streamingRef.current = true
      setActivity(null)
      setDrawer('none')

      const controller = new AbortController()
      abortRef.current = controller

      // Tokens arrive far faster than it is worth re-rendering. Each render
      // re-parses the whole growing answer through remark-gfm, so rendering per
      // token is O(n^2) and locks up the tab on long, table-heavy replies.
      // Buffer chunks and flush on a short interval instead.
      let buffer = ''
      let flushTimer: ReturnType<typeof setTimeout> | null = null

      const flush = () => {
        flushTimer = null
        if (!buffer) return
        const chunk = buffer
        buffer = ''
        updateActive(s => {
          const msgs = [...s.messages]
          const last = msgs[msgs.length - 1]
          if (last?.role === 'assistant') {
            msgs[msgs.length - 1] = { ...last, content: last.content + chunk }
          }
          return { ...s, messages: msgs, updatedAt: Date.now() }
        })
      }

      const appendToAssistant = (chunk: string) => {
        buffer += chunk
        if (!flushTimer) flushTimer = setTimeout(flush, 90)
      }

      const cancelFlush = () => {
        if (flushTimer) {
          clearTimeout(flushTimer)
          flushTimer = null
        }
      }

      await streamAiChat(
        history,
        {
          onToken: chunk => {
            setActivity(null)
            appendToAssistant(chunk)
          },
          onReset: () => {
            cancelFlush()
            buffer = ''
            updateActive(s => {
              const msgs = [...s.messages]
              const last = msgs[msgs.length - 1]
              if (last?.role === 'assistant') {
                msgs[msgs.length - 1] = { ...last, content: '' }
              }
              return { ...s, messages: msgs }
            })
          },
          onSummary: summary => updateActive(s => ({ ...s, summary })),
          onHandover: () => {
            setHandledByHuman(true)
            // Drop the empty assistant bubble - a human will answer instead.
            updateActive(s => ({
              ...s,
              messages: s.messages.filter(
                (m, i) =>
                  !(
                    i === s.messages.length - 1 &&
                    m.role === 'assistant' &&
                    !m.content
                  )
              ),
            }))
          },
          onTool: (name, status) => {
            setActivity(
              status === 'running' ? TOOL_LABELS[name] ?? 'Working' : null
            )
          },
          onError: message => {
            setActivity(null)
            appendToAssistant(`\n\n⚠️ ${message}`)
          },
          onDone: () => {
            cancelFlush()
            flush()
            setStreaming(false)
            streamingRef.current = false
            setActivity(null)
            loadTickets()
            updateActive(s => {
              const msgs = [...s.messages]
              const last = msgs[msgs.length - 1]
              if (last?.role === 'assistant') {
                msgs[msgs.length - 1] = {
                  ...last,
                  content:
                    last.content.trim() ||
                    'I was not able to produce an answer. Please try rephrasing.',
                }
              }
              return { ...s, messages: msgs, updatedAt: Date.now() }
            })
          },
        },
        controller.signal,
        summaryAtSend,
        active.id
      )
    },
    [active, streaming, updateActive, loadTickets, pending]
  )

  useEffect(() => {
    sendRef.current = send
  }, [send])

  const stop = () => {
    abortRef.current?.abort()
    setStreaming(false)
    setActivity(null)
  }

  // Without a configured endpoint the panel would only ever show errors.
  // Also hidden until the account is known, so chats can never be shown
  // before they have been scoped to the right user.
  if (!env.aiUrl || !userKey) return null

  if (!open) {
    return (
      <IconButton
        className={classes.launcher}
        onClick={() => setOpen(true)}
        aria-label="Open AI assistant"
        size="large"
      >
        <AssistantIcon />
      </IconButton>
    )
  }

  const messages = active?.messages ?? []

  return (
    <Box className={classes.panel} style={{ width: `${width}px` }}>
      <Box
        className={classes.resizeHandle}
        onMouseDown={startResize}
        role="separator"
        aria-label="Resize assistant panel"
      />

      <Box className={classes.header}>
        <Box display="flex" alignItems="center" gap="8px" minWidth={0}>
          <AssistantIcon className={classes.headerIcon} />
          <span className={classes.headerTitle}>MAS Support Agent</span>
        </Box>
        <Box display="flex" alignItems="center">
          <Tooltip title="Previous chats">
            <IconButton
              onClick={() => setDrawer(d => (d === 'chats' ? 'none' : 'chats'))}
              aria-label="Show previous chats"
              size="small"
            >
              <HistoryIcon className={classes.headerAction} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Tickets">
            <IconButton
              onClick={() =>
                setDrawer(d => (d === 'tickets' ? 'none' : 'tickets'))
              }
              aria-label="Show my tickets"
              size="small"
            >
              <Box className={classes.ticketBtn}>
                <ConfirmationNumberIcon className={classes.headerAction} />
                {openTicketCount > 0 && (
                  <span className={classes.ticketBadge}>{openTicketCount}</span>
                )}
              </Box>
            </IconButton>
          </Tooltip>
          <Tooltip title="New chat">
            <span>
              <IconButton
                onClick={createSession}
                disabled={streaming}
                aria-label="Start a new chat"
                size="small"
              >
                <AddIcon className={classes.headerAction} />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            onClick={() => setOpen(false)}
            aria-label="Close AI assistant"
            size="small"
          >
            <CloseIcon className={classes.headerAction} />
          </IconButton>
        </Box>
      </Box>

      {drawer === 'chats' && (
        <Box className={classes.historyList}>
          <Box className={classes.historyHint}>
            Last {MAX_SESSIONS} chats on this device
          </Box>
          {sessions.map(session => (
            <Box
              key={session.id}
              className={
                session.id === activeId
                  ? `${classes.historyItem} ${classes.historyItemActive}`
                  : classes.historyItem
              }
            >
              <Box
                className={classes.historyItemLabel}
                onClick={() => {
                  if (streaming) return
                  setActiveId(session.id)
                  setDrawer('none')
                }}
              >
                {session.title}
              </Box>
              <IconButton
                onClick={() => deleteSession(session.id)}
                aria-label={`Delete chat: ${session.title}`}
                size="small"
              >
                <DeleteIcon className={classes.historyDelete} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {drawer === 'tickets' && (
        <Box className={classes.ticketsView}>
          <Box className={classes.ticketsHead}>
            <span className={classes.ticketsTitle}>Your tickets</span>
            <button
              type="button"
              className={classes.ticketsBack}
              onClick={() => setDrawer('none')}
            >
              Back to chat
            </button>
          </Box>

          <Box className={classes.ticketsBody}>
            {tickets.length === 0 ? (
              <Box className={classes.ticketEmpty}>
                You have no tickets yet.
                <br />
                <br />
                Ask me to escalate something, or type <code>/ticket</code>{' '}
                followed by the problem and your app key — for example:
                <br />
                <code>/ticket ads not serving, app key aBc123XyZ</code>
              </Box>
            ) : (
              tickets.map(t => (
                <Box key={t.id} className={classes.ticketCard}>
                  <Box className={classes.ticketTop}>
                    <span className={classes.ticketRef}>{t.reference}</span>
                    <span
                      className={`${classes.ticketStatus} ${
                        t.status === 'done'
                          ? classes.statusDone
                          : t.status === 'ongoing'
                          ? classes.statusOngoing
                          : classes.statusOpen
                      }`}
                    >
                      {t.status}
                    </span>
                  </Box>
                  <Box className={classes.ticketSubject}>{t.subject}</Box>
                  {t.appKey && (
                    <Box className={classes.ticketMeta}>{t.appKey}</Box>
                  )}
                  {t.reply && (
                    <Box className={classes.ticketReply}>
                      <span className={classes.ticketReplyWho}>Yodo1 team</span>
                      {t.reply.text}
                    </Box>
                  )}
                </Box>
              ))
            )}
          </Box>
        </Box>
      )}

      {drawer === 'tickets' ? null : !consented ? (
        <Box className={classes.consent}>
          <p className={classes.consentTitle}>Before you start</p>
          <p className={classes.consentText}>
            Your conversations with this assistant are recorded and stored for
            30 days, after which they are deleted automatically.
          </p>
          <p className={classes.consentText}>
            The Yodo1 team can read them to improve the assistant and to help
            with anything you escalate. Messages may include game names, app
            keys and performance figures from your account, so please do not
            paste passwords, API keys or other credentials.
          </p>
          <p className={classes.consentText}>
            Answers are generated by an AI model and can be wrong — check
            anything important against the dashboard.
          </p>
          <button
            type="button"
            className={classes.consentButton}
            onClick={() => {
              recordConsent(userKey)
              setConsented(true)
            }}
          >
            OK, got it
          </button>
        </Box>
      ) : (
        <Box className={classes.body} ref={scrollRef}>
          {handledByHuman && (
            <Box className={classes.handoverBanner}>
              A member of the Yodo1 team has joined this conversation and will
              reply here.
            </Box>
          )}
          {messages.length === 0 && (
            <Box className={classes.empty}>
              <p className={classes.emptyTitle}>
                Ask about your ad monetization performance or integration setup.
              </p>
              {SUGGESTIONS.map(suggestion => (
                <button
                  key={suggestion}
                  type="button"
                  className={classes.suggestion}
                  onClick={() => send(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </Box>
          )}

          {messages.map((message, index) => (
            <Box
              key={index}
              className={
                message.role === 'user' ? classes.userRow : classes.assistantRow
              }
            >
              <MessageBubble
                role={message.role}
                content={message.content}
                at={message.at}
                images={message.images}
                classes={classes as unknown as Record<string, string>}
                isStreaming={streaming && index === messages.length - 1}
              />
            </Box>
          ))}

          {activity && (
            <Box className={classes.activity}>
              <span className={classes.activityDot} />
              {activity}…
            </Box>
          )}
        </Box>
      )}

      {(pending.length > 0 || imgError) && (
        <Box className={classes.pendingBar}>
          {pending.map((img, i) => (
            <Box key={i} className={classes.pendingThumb}>
              <img src={img.url} alt={img.name} />
              <button
                type="button"
                aria-label={`Remove ${img.name}`}
                onClick={() => setPending(p => p.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </Box>
          ))}
          {imgError && <span className={classes.imgError}>{imgError}</span>}
        </Box>
      )}

      <Box className={classes.footer}>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={async e => {
            const files = e.target.files
            if (!files?.length) return
            const { images, error } = await pickImages(files, pending.length)
            setPending(p => [...p, ...images].slice(0, MAX_IMAGES))
            setImgError(error)
            // Allow re-picking the same file after removing it.
            e.target.value = ''
          }}
        />
        <Tooltip title={`Attach image (up to ${MAX_IMAGES})`}>
          <span>
            <IconButton
              onClick={() => fileRef.current?.click()}
              disabled={streaming || pending.length >= MAX_IMAGES}
              aria-label="Attach image"
              size="small"
            >
              <ImageIcon className={classes.sendIcon} />
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          value={input}
          placeholder="Ask about revenue, eCPM, ARPDAU…"
          // Grammarly attaches a heavy observable chain to text fields, which
          // fires on every keystroke and every DOM change around it.
          inputProps={{ 'data-gramm': 'false', 'data-gramm_editor': 'false' }}
          onChange={event => setInput(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              if (input.trim() || pending.length > 0) {
                send(input || 'Please look at this screenshot.')
              }
            }
          }}
          disabled={streaming}
        />
        {streaming ? (
          <button type="button" className={classes.stopButton} onClick={stop}>
            Stop
          </button>
        ) : (
          <IconButton
            onClick={() => send(input || 'Please look at this screenshot.')}
            disabled={!input.trim() && pending.length === 0}
            aria-label="Send message"
            size="small"
          >
            <SendIcon className={classes.sendIcon} />
          </IconButton>
        )}
      </Box>
    </Box>
  )
}

export default AiAssistant
